import { fetchSerialNumberLocators } from '@gn-audio/core/api/knowledge-base/fetch-serial-number-locators';
import { ISerialNumberLocator } from '@gn-audio/core/api/knowledge-base/models/serial-number-locator';
import { IFromPrice, ISFCCFamilyPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { getProductCatalogueApiConfig } from '@gn-audio/core/api/product-catalogue';
import { fetchGroup, fetchGroupFeatures, IGroup } from '@gn-audio/core/api/product-catalogue';
import { IFeature, ISearchItem, ISFCCSearchItem } from '@gn-audio/core/api/product-catalogue/models';
import { ProductType, ProductAttributeKey } from "@gn-audio/core/common/enums";
import { Component, Inject, Input, OnInit } from 'angular-ts-decorators';

import { ISegmentTypeWebFormSetting, PfmMode, PfmModeIndex } from '../models';
import { getAttributeValue } from '../utils/get-attribute-value';
import template from './product-item.component.html';

@Component({
  selector: 'productItem',
  template,
})
export class ProductItemComponent implements OnInit {
  public static $inject: ReadonlyArray<string> = ['$q'];

  @Input() public contactSupportConfigurations?: ISegmentTypeWebFormSetting[];
  @Input('@') public mode?: PfmModeIndex;
  @Input() public product?: ISearchItem | ISFCCSearchItem;

  @Input('@') public productType?: ProductType;

  @Input() public showSpecialOffers = false;

  public price?: IFromPrice | ISFCCFamilyPrice | ISFCCGroupPrice | null;
  public serialNumberLocatorUrl?: string;
  public bluetoothPairingGuideUrl?: string;

  constructor(@Inject('$q') private $q: ng.IQService) { }

  public ngOnInit() {
    if (this.isGroupType && getProductCatalogueApiConfig().isSFCCSearchOn) {
      this.$q.when(this.populateProductWithAdditionalInfo())
        .then(() => { this.initialize(); });
    } else {
      this.initialize();
    }
  }

  private initialize() {
    if (this.hasSupportUrl && this.isGroupType) {
      const { hasBluetoothPairingGuide, supportPageUrl } = this.product as ISearchItem;
      const { groupId, sku } = this.product;

      if (this.isSupportMode) {
        this.$q.when(fetchSerialNumberLocators(groupId))
          .then((serialNumberLocators: ISerialNumberLocator[] = []) => {
            if (serialNumberLocators.length === 0) {
              return;
            }

            this.serialNumberLocatorUrl = `${supportPageUrl}#/SERVICES#${sku}`;
          });
      }

      if (this.isBluetoothPairingGuideMode && hasBluetoothPairingGuide) {
        this.bluetoothPairingGuideUrl = `${supportPageUrl}/${sku}/get-started#BTPG`;
      }
    }
  }

  public get hasCouponCalloutMessage(): boolean {
    const price = this.price as ISFCCGroupPrice | ISFCCFamilyPrice;
    return !!price?.couponCalloutMessage;
  }

  public get canShowCalloutMessage(): boolean {
    return (this.isNormalMode || this.isCompareMode) && !this.hasCouponCalloutMessage
  }

  public get hasProduct() {
    return Boolean(this.product);
  }

  public get productId(): number {
    if (!this.hasProduct) {
      return 0;
    }

    return this.isGroupType ? this.product.groupId : this.product.familyId;
  }

  public productUrl(): string {
    if (!this.hasProduct) {
      return 'javascript:;';
    }

    switch (this.mode) {
      case PfmMode.Normal:
      case PfmMode.Compare:
        return this.product.pageUrl;
      case PfmMode.BluetoothPairingGuide:
        return this.bluetoothPairingGuideUrl;
      default:
        return (this.product as ISearchItem)!.supportPageUrl;
    }
  }

  public onPriceLoaded(price?: IFromPrice | ISFCCFamilyPrice | ISFCCGroupPrice | null) {
    this.price = price;
  }

  public get isFamilyType(): boolean {
    return this.productType === ProductType.Family;
  }

  public get isGroupType(): boolean {
    return this.productType === ProductType.Group;
  }

  public get isNormalMode(): boolean {
    return this.mode === PfmMode.Normal;
  }

  public get isBluetoothPairingGuideMode(): boolean {
    return this.mode === PfmMode.BluetoothPairingGuide;
  }

  public get isContactSupportMode(): boolean {
    return this.mode === PfmMode.ContactSupport;
  }

  public get isSupportMode(): boolean {
    return this.mode === PfmMode.Support;
  }

  public get isCompareMode(): boolean {
    return this.mode === PfmMode.Compare;
  }

  public get hasHappyFoxId(): boolean {
    return this.happyFoxId !== 0;
  }

  public happyFoxWebformUrl(): string {
    if (!this.hasHappyFoxId) {
      return 'javascript:;';
    }

    if (!Array.isArray(this.contactSupportConfigurations)) {
      return 'javascript:;';
    }

    const webFormConfiguration: ISegmentTypeWebFormSetting = this.contactSupportConfigurations
      .filter((c: ISegmentTypeWebFormSetting): boolean =>
        (!c.segmentType || c.segmentType === this.product.segmentType) &&
        (!c.productType || c.productType === getAttributeValue(this.product.attributes, ProductAttributeKey.MetaProductType)),
      )[0];

    if (!webFormConfiguration) {
      return 'javascript:;';
    }

    return `${webFormConfiguration.webFormUrl}?happyFoxId=${this.happyFoxId}&productId=${this.productId}`;
  }

  private get happyFoxId(): number {
    const happyFoxId: string = getAttributeValue(this.product.attributes, ProductAttributeKey.HappyfoxProductId);
    return Number(happyFoxId) || 0;
  }

  private get hasSupportUrl(): boolean {
    return this.hasProduct && Boolean((this.product as ISearchItem)?.supportPageUrl);
  }

  private async populateProductWithAdditionalInfo(): Promise<void> {
    const productAdditionalInfo: IGroup | null = await fetchGroup(this.productId);
    const productFeatures: IFeature[] | null = await fetchGroupFeatures(this.productId);

    this.product = {
      ...this.product,
      uniqueSellingPoint: productAdditionalInfo?.uniqueSellingPoint || '',
      features: (productFeatures || []).map(f => f.text || f.header),
    };
  }
}
